// Service for handling appointment rescheduling

export interface RescheduleRequest {
  email: string;
}

export interface ConfirmRescheduleRequest {
  email: string;
  old_date: string; // YYYY-MM-DD format
  old_time: string; // HH:MM format
  new_date: string; // YYYY-MM-DD format
  new_time: string; // HH:MM format
}

export interface RescheduleResponse {
  success: boolean;
  message: string;
  error?: string;
}

export async function submitRescheduleRequest(
  rescheduleData: RescheduleRequest
): Promise<RescheduleResponse> {
  try {
    console.log('Submitting reschedule request:', rescheduleData);
    
    const webhookPayload = {
      request_type: 'reschedule_request',
      user_email: rescheduleData.email
    };

    const response = await fetch('https://hook.eu2.make.com/tau851j2pxe2j9wr1l6pu94wqve60bw2', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(webhookPayload),
    });

    if (!response.ok) {
      console.error('Webhook failed:', response.status, response.statusText);
      return {
        success: false,
        message: 'Failed to process reschedule request',
        error: 'Failed to process reschedule request'
      };
    }

    console.log('Reschedule request webhook triggered successfully');
    return {
      success: true,
      message: 'Your reschedule request has been submitted. You will receive a confirmation email shortly.'
    };
  } catch (error) {
    console.error('Error submitting reschedule request:', error);
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        error: 'Network connection failed'
      };
    }
    
    return {
      success: false,
      message: 'An unexpected error occurred. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

export async function confirmReschedule(
  confirmationData: ConfirmRescheduleRequest
): Promise<RescheduleResponse> {
  try {
    console.log('Confirming reschedule:', confirmationData);
    
    const webhookPayload = {
      request_type: 'reschedule_confirmed',
      user_email: confirmationData.email,
      old_date: confirmationData.old_date,
      old_time: confirmationData.old_time,
      new_date: confirmationData.new_date,
      new_time: confirmationData.new_time
    };

    const response = await fetch('https://hook.eu2.make.com/tau851j2pxe2j9wr1l6pu94wqve60bw2', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(webhookPayload),
    });

    if (!response.ok) {
      console.error('Webhook failed:', response.status, response.statusText);
      return {
        success: false,
        message: 'Failed to confirm reschedule',
        error: 'Failed to confirm reschedule'
      };
    }

    console.log('Reschedule confirmation webhook triggered successfully');
    return {
      success: true,
      message: 'Your appointment has been successfully rescheduled.'
    };
  } catch (error) {
    console.error('Error confirming reschedule:', error);
    return {
      success: false,
      message: 'An unexpected error occurred. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

export function validateRescheduleData(data: RescheduleRequest): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.email?.trim()) {
    errors.push('Email address is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Valid email address is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}